import React, { useState, useEffect, useCallback } from 'react';
import { growthDelegationsAPI, usersAPI } from '../services/api';
import { FiRefreshCw, FiAlertTriangle, FiUserPlus, FiX, FiTrash2 } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import StandInCovers from './StandInCovers';
import './Students.css';
import './ClientVertical.css';

const GrowthAccess = () => {
  const { isCeoOrCoo } = useAuth();
  const [delegations, setDelegations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pick, setPick] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [dRes, eRes] = await Promise.all([
        growthDelegationsAPI.list(),
        usersAPI.permanentEmployees().catch(() => ({ data: [] })),
      ]);
      setDelegations(dRes.data.results || dRes.data || []);
      setEmployees(eRes.data.results || eRes.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load growth-access delegations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      await growthDelegationsAPI.create(parseInt(pick, 10));
      setPick('');
      load();
    } catch (e) {
      setError(e?.response?.data?.delegated_user?.[0] || JSON.stringify(e?.response?.data) || 'Failed to grant access.');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (d) => {
    try { await growthDelegationsAPI.setActive(d.id, !d.is_active); load(); }
    catch { setError('Failed to update.'); }
  };

  const revoke = async (d) => {
    if (!window.confirm(`Revoke ${d.delegated_user_name}'s access to Agencies & Sub-Agent Applications?`)) return;
    try { await growthDelegationsAPI.remove(d.id); load(); }
    catch { setError('Failed to revoke.'); }
  };

  const grantedIds = new Set(delegations.map((d) => d.delegated_user));
  const available = employees.filter((e) => !grantedIds.has(e.id));

  return (
    <div className="students-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiUserPlus size={22} color="#011ff3" /> Growth Access
          </h1>
          <p style={{ margin: '6px 0 0', color: '#94a3b8', fontSize: 14, maxWidth: 620 }}>
            The executive board always sees <b>Agencies</b> and <b>Sub-Agent Applications</b>. Grant a Permanent Employee access here when they need to help run the B2B pipeline. Approving applications and suspending agencies stays exec-board only.
          </p>
        </div>
        <button className="btn-icon" onClick={load} title="Refresh">
          <FiRefreshCw size={15} className={loading ? 'cv-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="error-message" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <FiAlertTriangle size={18} /><span style={{ flex: 1, wordBreak: 'break-word' }}>{error}</span>
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
      )}

      <div className="cv-subsection" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ color: '#666', fontSize: 13 }}>Grant access to</span>
        <select className="filter-select" value={pick} onChange={(e) => setPick(e.target.value)} style={{ minWidth: 240 }}>
          <option value="">Select a Permanent Employee…</option>
          {available.map((e) => (
            <option key={e.id} value={e.id}>{(`${e.first_name || ''} ${e.last_name || ''}`).trim() || e.username}</option>
          ))}
        </select>
        <button className="btn-primary" onClick={grant} disabled={!pick || busy} style={{ minWidth: 'auto' }}>
          {busy ? 'Granting…' : 'Grant'}
        </button>
        {available.length === 0 && !loading && <span style={{ color: '#94a3b8', fontSize: 12 }}>All Permanent Employees already have a delegation row.</span>}
      </div>

      <div className="students-table-container">
        {loading ? (
          <div className="empty-state">Loading…</div>
        ) : delegations.length === 0 ? (
          <div className="empty-state">No delegations yet — only the executive board can see the growth stack.</div>
        ) : (
          <table className="students-table">
            <thead>
              <tr>{['Employee', 'Granted by', 'Status', ''].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {delegations.map((d) => (
                <tr key={d.id}>
                  <td>
                    <strong>{d.delegated_user_name}</strong>
                    <div style={{ color: '#94a3b8', fontSize: 11 }}>{d.delegated_user_role}</div>
                  </td>
                  <td>{d.delegated_by_name}</td>
                  <td>
                    <button onClick={() => toggle(d)} className="status-pill"
                      style={{ color: d.is_active ? '#43e97b' : '#94a3b8', cursor: 'pointer', background: d.is_active ? 'rgba(67,233,123,0.08)' : '#f5f7fa' }}>
                      {d.is_active ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td>
                    <button onClick={() => revoke(d)} title="Revoke" className="btn-icon"><FiTrash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isCeoOrCoo() && <StandInCovers />}
    </div>
  );
};

export default GrowthAccess;
